import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConsumptionFile } from 'src/app/filemanagement/UdFile';
import { ValidationMessageCode } from '../../models/validation-message-codes';
import { UploadErrorEvent } from 'src/app/filemanagement/components/upload/upload.component';
import { CustomService } from '../../services/custom.service';
import { Subscription } from 'rxjs';
import { PricingSourceService } from '@app/shared/services/pricing-source.service';

@Component({
  selector: 'app-fix-data',
  templateUrl: './fix-data-component.html',
  styleUrls: ['./fix-data-component.scss'],
})
export class FixDataComponent implements OnInit, OnDestroy {
  public title: string;
  public description: string;
  public messages: string[];
  public radioTxts: string[] = [];
  public hideUpload = false;
  public parseRequestData: any;
  public consumptionFile: ConsumptionFile;
  public parsingErrorMessages: string[] = [];
  setForecastedSubscription$: Subscription;

  constructor(
    public dialogRef: MatDialogRef<FixDataComponent>,
    public customQuoteService: CustomService,
    private pricingSourceService: PricingSourceService,
    @Inject(MAT_DIALOG_DATA) public dialogData: FixDataDialogData
  ) {
    dialogData.parseRequestData.force = true;
    this.parseRequestData = dialogData.parseRequestData;
    this.setFixTitleDescription(dialogData.validationMessage);
  }

  ngOnDestroy(): void {
    this.setForecastedSubscription$?.unsubscribe();
  }

  ngOnInit() {}

  public onCompleteUpload(files: ConsumptionFile[]) {
    // Map parsing error messages to table
    files.forEach((file) =>
      file.data.errors.forEach((err) => {
        this.parsingErrorMessages.unshift(err.message);
      })
    );

    files.forEach((file) => {
      if (!file.data.data) {
        return;
      }
      const foundUan = file.data.data.find((d) => d.meterIdentifier === this.dialogData.utilityAccountNumber);
      if (!foundUan) {
        this.parsingErrorMessages.unshift(
          `${file.name} - Could not find Utility Account Number ${this.dialogData.utilityAccountNumber} in file.`
        );
      } else {
        this.consumptionFile = { ...file, fileId: file.data.fileId }; // Valid file is displayed.
      }
    });
  }

  public onUploadError(error: UploadErrorEvent) {
    error.files.forEach((file) => {
      if (error.errorCode === 'WrongFileType') {
        this.parsingErrorMessages.unshift(`${file.name} - File type not supported.`);
      } else if (file.error.includes('maximum file size')) {
        this.parsingErrorMessages.unshift(`${file.name} - Maximum file size exceeded.`);
      } else {
        this.parsingErrorMessages.unshift(`${file.name} - Could not parse file.`);
      }
    });
  }

  public onDeleteUpload() {
    this.consumptionFile = null;
  }

  public setFixTitleDescription(message: string) {
    let messages = message.split('\n');
    if (messages.length > 1) {
      messages = messages.map((x) => (x = '- ' + x));
      this.title = `Fix Errors - for ${this.dialogData.utilityAccountNumber}`;
      this.messages = messages;
      return;
    }

    message = messages[0];

    this.pricingSourceService.isDynamicPricingSource().subscribe((isDynamicPricing) => {
      if (isDynamicPricing && ValidationMessageCode.LESS_THAN_60_DAYS.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Missing Usage Data`;
        this.description =
          'This utility account number has less than 60 days of usage data associated with it. Please  upload the usage data for this utility account number below to proceed.';
      }
      if (isDynamicPricing && ValidationMessageCode.LESS_THAN_365_DAYS.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Missing Usage Data`;
        this.description =
          'This utility account number has less than 365 days of usage data associated with it. Please  upload the usage data for this utility account number below to proceed, OR if you do not have enough usage history for the supplier to generate a price for you, we can forecast your energy usage for future periods to provide you with a price.';
        this.radioTxts = ['I would like to upload more usage data', 'I would like missing usage data to be forecasted'];
      }

      if (ValidationMessageCode.FUTURE_DATE.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Future Dates`;
        this.description =
          'This usage file for this utility account number contains dates in the future. Please amend and re-upload the usage data for this utility account number below to proceed.';
      }
      if (ValidationMessageCode.NO_DATA.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - No Usage Data`;
        this.description =
          'This utility account number has no usage data associated with it. Please upload at least 365 days of usage data for this utility account number to proceed.';
      }

      if (
        (!isDynamicPricing && ValidationMessageCode.LESS_THAN_365_DAYS.startsWith(message)) ||
        ValidationMessageCode.LESS_THAN_60_DAYS.startsWith(message)
      ) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Missing Usage Data`;
        this.description =
          'This utility account number has less than 365 days of usage data associated with it. Please  upload the usage data for this utility account number below to proceed.';
      }
      if (ValidationMessageCode.TOO_MANY_GAPS.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Too Many Gaps`;
        this.description = `This utility account number has more than ${this.dialogData.gapsCriteria} gaps. Please amend and re-upload the usage data for this utility account number below to proceed.`;
      }
      if (ValidationMessageCode.DATA_TOO_OLD.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Usage Data is Out of Date`;
        this.description =
          'The usage data is out of date. Please amend and re-upload the usage data for this utility account number below to proceed.';
      }
      if (ValidationMessageCode.MANDATORY_DATA_MISSING.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Missing Mandatory Data`;
        this.description =
          'This utility account number is missing mandatory data. Please amend and re-upload the usage data for this utility account number below to proceed.';
      }
      if (ValidationMessageCode.TOO_MANY_OVERLAPPING_DATES.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Overlapping Dates`;
        this.description =
          'There are overlapping dates for Usage End Date and Usage Start Date for this utility account number. Please amend and re-upload the usage data for this utility account number below to proceed.';
      }
      if (ValidationMessageCode.GAPS_EXIST.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Gaps Between Dates`;
        this.description =
          'There are one or more gaps between the Usage End Date and the next Usage Start Date for this utility account number. Please amend and re-upload the usage data for this utility account number below to proceed.';
      }
      if (ValidationMessageCode.INVALID_DATE_FORMAT.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Invalid Date Format`;
        this.description =
          'There are invalid dates in the usage data for this utility account number.  Please amend the format for all entries to MM/DD/YYYY and re-upload the usage data for this utility account number below to proceed';
      }
      if (ValidationMessageCode.TOO_MANY_ZEROS.startsWith(message)) {
        this.title = `Fix Error for ${this.dialogData.utilityAccountNumber} - Too Many Zeros`;
        this.description = `This utility account number has more than ${this.dialogData.zerosCriteria} zeros.  Please amend and re-upload the usage data for this utility account number to proceed.`;
      }
    });
  }

  public onRadioChanged(value: boolean) {
    this.hideUpload = value;
  }

  public closeDialog(forecasted: boolean) {
    var errorMsg = "Please upload a usage data file";
    if (forecasted) {
      this.setForecastedSubscription$ = this.customQuoteService
        .setForecasted(this.dialogData.quoteRequestId, this.dialogData.utilityAccountNumber, true)
        .subscribe(() => this.dialogRef.close());
    } else {
      if (this.consumptionFile == null) {
        if (this.parsingErrorMessages.indexOf(errorMsg) == -1)
          this.parsingErrorMessages.push(errorMsg);
      }
      else
        this.dialogRef.close();
    }
  }

  public getParsingErrorMessages() {
    return this.parsingErrorMessages.map((x) => {
      if (x.endsWith('.')) {
        return x.substring(0, x.length - 1);
      } else {
        return x;
      }
    });
  }
}

export class FixDataDialogData {
  public pricingMethod: string;
  public quoteRequestId: number;
  public utilityAccountNumber: string;
  public gapsCriteria: number;
  public zerosCriteria: number;
  public validationMessage: string;
  public parseRequestData: {
    parserName: string;
    quoteRequestId: number;
    utilityAccountNumber: string;
    force: boolean;
  };
}
